// ─── Per-route crawler content + structured data ─────────────────────────
//
// The marketing site is a client-rendered SPA. Crawlers that do not execute
// JavaScript (Meta's facebookexternalhit / meta-externalads / meta-externalagent,
// LinkedIn, Slack, iMessage, most AI fetchers) only ever see index.html's
// <noscript> fallback — so without help every route reads as the homepage.
//
// This module is the single source of truth for what those crawlers get per
// route. scripts/prerender-routes.mjs bundles it at build time and writes a
// route-specific <noscript> body plus a JSON-LD <script> into each route's
// static HTML. SEOHead reads the same `jsonLd` at runtime so the JS-rendered
// page and the crawler HTML carry identical structured data.
//
// Pages import their FAQ / step / pillar copy from here rather than defining
// it locally, so the text a crawler reads is the text a visitor reads.
//
// Add an entry whenever you add a route to src/App.tsx and src/lib/route-seo.json
// — the prerender fails the build if a configured route has no content here.

import {
  PLANS,
  MAX_ADS_PER_BULK_PUBLISH,
  FAQ_ITEMS as PRICING_FAQ_ITEMS,
  PRICING_JSON_LD,
  TRIAL_DAYS,
  TRIAL_SHORT,
  TRIAL_PLAN_NAME,
  GUARANTEE_DAYS,
  GUARANTEE_SHORT,
  MAX_CONNECTED_ACCOUNTS,
  PRICE_SUMMARY,
  PRICES_AS_OF_LABEL,
  RENEWAL_TERMS,
  formatPrice,
  formatUsd,
} from "./pricing-config";

export const SITE_URL = "https://sidestepads.com";
export const SUPPORT_EMAIL = "support@sidestepads.com";
export const SUPPORT_PHONE_DISPLAY = "(503) 530-0339";
export const SUPPORT_PHONE_E164 = "+1-503-530-0339";
export const SUPPORT_HOURS = "Monday - Friday: 9am - 5pm PST";
export const SUPPORT_LOCATION = "Portland, Oregon, USA";

// ─── Types ────────────────────────────────────────────────────────────────

export interface FaqItem {
  question: string;
  answer: string;
}

export interface CrawlerSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
  /** Render bullets as a numbered list (steps). */
  ordered?: boolean;
  /**
   * A table rendered after the paragraphs. `columns` includes the row-header
   * column; each row is [row header, ...cells].
   */
  table?: { caption: string; columns: string[]; rows: string[][] };
}

export interface RouteContent {
  /** Short label for the crawler nav / breadcrumbs. */
  navLabel: string;
  /** One-paragraph summary under the H1 (the H1 itself lives in route-seo.json). */
  intro: string;
  sections: CrawlerSection[];
  faq?: FaqItem[];
  /** Structured data for this route — baked into the static HTML and used by SEOHead. */
  jsonLd: object;
}

// ─── Shared helpers ───────────────────────────────────────────────────────

const breadcrumb = (...trail: Array<[name: string, path: string]>) => ({
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    ...trail.map(([name, path], i) => ({
      "@type": "ListItem",
      position: i + 2,
      name,
      item: `${SITE_URL}${path}`,
    })),
  ],
});

const faqPage = (items: FaqItem[]) => ({
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
});

const usd = (n: number) => `$${n.toLocaleString("en-US")}`;
const STARTING_PRICE = `${usd(PLANS[0].monthlyPrice)}/mo`;
const MAX_BULK_PUBLISH = MAX_ADS_PER_BULK_PUBLISH;

const n = (v: number) => v.toLocaleString("en-US");
const plural = (v: number, one: string, many: string) => `${n(v)} ${v === 1 ? one : many}`;

/** "Starter — $39/mo ($374.40/yr billed annually): 3 connected ad accounts (Meta + Google Ads combined), 1 team seat, …" */
const planSummaryLines = PLANS.map(
  (p) =>
    `${p.name} — ${usd(p.monthlyPrice)}/mo (${formatUsd(p.annualPrice)}/yr billed annually): ` +
    `${plural(p.connectedAccounts, "connected ad account", "connected ad accounts")} (Meta + Google Ads combined), ` +
    `${plural(p.seats, "team seat", "team seats")}, ${n(p.aiCreditsPerMonth)} AI credits a month, ` +
    `${n(p.adsPerBulkPublish)} ads per bulk publish, ${n(p.adsPer24h)} ads per 24 hours. ${p.bestFor}.`,
);

/** The capacity comparison as a crawler table: one column per plan. */
const planComparisonTable: CrawlerSection["table"] = {
  caption: `Sidestep Ads plans compared (USD, prices as of ${PRICES_AS_OF_LABEL})`,
  columns: ["", ...PLANS.map((p) => p.name)],
  rows: [
    ["Monthly billing", ...PLANS.map((p) => `${usd(p.monthlyPrice)}/month`)],
    ["Yearly billing", ...PLANS.map((p) => `${formatUsd(p.annualPrice)}/year (${formatPrice(p, "annual")}/month)`)],
    ["Connected ad accounts (Meta + Google Ads combined)", ...PLANS.map((p) => n(p.connectedAccounts))],
    ["Team seats", ...PLANS.map((p) => n(p.seats))],
    ["Ads per bulk publish", ...PLANS.map((p) => n(p.adsPerBulkPublish))],
    ["Ads per 24 hours", ...PLANS.map((p) => n(p.adsPer24h))],
    ["AI credits per month", ...PLANS.map((p) => n(p.aiCreditsPerMonth))],
    ["Features", ...PLANS.map(() => "All")],
  ],
};

const pricingSection: CrawlerSection = {
  heading: "Pricing",
  paragraphs: [
    `Every new account starts with a ${TRIAL_SHORT} with full ${TRIAL_PLAN_NAME} access — no credit card required. Plans start at ${STARTING_PRICE}; every feature is included on every plan and capacity scales by tier. A separate ${GUARANTEE_SHORT} covers your first subscription payment.`,
  ],
  bullets: planSummaryLines,
};

// ─── Home ─────────────────────────────────────────────────────────────────

export const HOME_FAQ_ITEMS: FaqItem[] = [
  {
    question: "Which ad platforms does Sidestep support?",
    answer: "Meta Ads (Facebook & Instagram) and Google Ads. You connect your accounts via secure OAuth — Sidestep never sees your password, and you can revoke access from Meta or Google at any time.",
  },
  {
    question: "Is Sidestep for agencies or in-house teams?",
    answer: "Both. Agencies run every client's ad accounts from one workspace, with shared tasks, notes and team access. In-house teams use it to consolidate dashboards, share visibility, and reduce tool-switching.",
  },
  {
    question: "Does Sidestep AI use my real account data?",
    answer: "Yes. AI copy, keyword, and RSA recommendations are built from your connected account data and brand context, not from generic templates.",
  },
  {
    question: "How long does setup take?",
    answer: "Up and running in minutes. You authorize Meta and Google via secure OAuth, pick the accounts you want, and Sidestep syncs your campaigns, ad sets, and performance data automatically.",
  },
  {
    question: "Can I try Sidestep before committing?",
    answer: `Yes. Every new account gets a ${TRIAL_DAYS}-day free trial with full ${TRIAL_PLAN_NAME} access — no credit card required. After the trial you can choose Starter, Pro, or Agency.`,
  },
  {
    question: "Do I need to replace Ads Manager completely?",
    answer: "No. Sidestep publishes Meta ads into your existing campaigns and ad sets, so everything still shows up in Ads Manager, and it connects Google Ads for monitoring and optimization. Use Sidestep for building, monitoring, and optimizing, and keep Ads Manager for anything else.",
  },
];

/** One card in the homepage's capability carousel. */
export interface HomeCapability {
  /** Category name, matching the five outputs of the hero diagram. */
  category: "Launch" | "Manage" | "Monitor" | "Optimize" | "Collaborate";
  title: string;
  desc: string;
  features: string[];
}

/** One row of the homepage's "old way vs. the Sidestep way" comparison. */
export interface HomeComparisonRow {
  old: string;
  next: string;
  /** short form, shown inside the Sidestep window in the visual */
  short: string;
}

export const HOME_COMPARISON: HomeComparisonRow[] = [
  {
    old: "Checking each ad account one at a time",
    next: "Meta + Google performance for every account in one view",
    short: "Every account, one view",
  },
  {
    old: "Clicking through Ads Manager popups for every ad",
    next: `Build up to ${MAX_BULK_PUBLISH} ads at once in a spreadsheet-style builder`,
    short: `Up to ${MAX_BULK_PUBLISH} ads at once`,
  },
  {
    old: "Exporting CSVs so ChatGPT can see your data",
    next: "AI that pulls in your live data, brand context, goals and recent changes",
    short: "AI with your live data",
  },
  {
    old: "Spotting problems days too late",
    next: "Alerts the moment a metric crosses your threshold",
    short: "Threshold alerts",
  },
  {
    old: "Client notes and to-dos scattered across tools",
    next: "Tasks, notes and team access in one workspace",
    short: "Tasks, notes & team access",
  },
];

export const HOME_CAPABILITIES: HomeCapability[] = [
  {
    category: "Launch",
    title: "Launch ads in minutes, not hours",
    desc: `Build single ads or batch up to ${MAX_BULK_PUBLISH} at once, with guided campaign building and reusable templates.`,
    features: ["Bulk Ad Builder", "Campaign Builder", "Ad Templates"],
  },
  {
    category: "Manage",
    title: "Change budgets and statuses in a few clicks",
    desc: "Pause, enable and adjust budgets across every connected account from one place.",
    features: ["Status Changes", "Budget Edits"],
  },
  {
    category: "Monitor",
    title: "Every account's performance in one view",
    desc: "Cross-platform dashboards, alerts the moment a metric crosses your threshold, and scheduled reports.",
    features: ["Dashboards", "Performance Alerts", "Scheduled Reports"],
  },
  {
    category: "Optimize",
    title: "AI grounded in your real account data",
    desc: "Recommendations built on your live performance, brand context and goals. Review, then apply in one click.",
    features: ["Keyword Optimizer", "Search Ad Analyzer", "Ad Copywriter", "AI Insights"],
  },
  {
    category: "Collaborate",
    title: "Keep the team and the work together",
    desc: "Tasks, notes and team access sit next to the accounts they're about, not in another tool.",
    features: ["Tasks", "Team Notes", "Team Access"],
  },
];

export interface HomeStep {
  title: string;
  desc: string;
}

// HOME_STEPS, the three steps in the homepage's closing call to action, is
// derived from HOW_IT_WORKS_STEPS below, so the homepage summary and the
// How It Works page can't drift apart.

export interface HomeAudience {
  title: string;
  desc: string;
  outcome: string;
}

export const HOME_AUDIENCES: HomeAudience[] = [
  {
    title: "Agencies",
    desc: "Run every client's ad accounts from one workspace, with shared tasks, notes and team access for cleaner handoffs.",
    outcome: "Scale without the mess",
  },
  {
    title: "Performance Marketers",
    desc: "Launch faster, spot issues earlier, and iterate on what's working — all from one view.",
    outcome: "Speed + visibility",
  },
  {
    title: "In-House Teams",
    desc: "Keep collaboration and optimization in one workflow. Shared dashboards, alerts, and AI tools.",
    outcome: "Aligned + efficient",
  },
  {
    title: "Founders & Operators",
    desc: "Get ad visibility without living inside ad platforms. See what's working in minutes.",
    outcome: "Clarity without complexity",
  },
];

// ─── How It Works ─────────────────────────────────────────────────────────

// Every claim below was checked against the app (RFlagg88/sidestep): OAuth
// connections with an account picker, tokens encrypted at rest and deleted on
// disconnect, Meta bulk publishing, Google search ads published paused, AI
// changes applied only after approval, and email invites with workspace-wide
// roles. Don't add timings ("live in N minutes") or sync intervals: the app
// fetches performance when a page opens rather than on a schedule.

export interface HowItWorksStep {
  /** Anchor id on the How It Works page. */
  id: "connect" | "create-monitor" | "optimize";
  /** Short name, used for the homepage's three steps and the step labels. */
  label: string;
  /** One-line summary, shown under the label on the homepage. */
  summary: string;
  /** The step's heading on the How It Works page (and its HowTo step name). */
  title: string;
  desc: string;
  bullets: string[];
  /** The step's poster card: a benefit headline, one line, and feature names. */
  poster: { headline: string; line: string; features: string[] };
}

/** The hero's subtitle, and the crawler intro under the H1. */
export const HOW_IT_WORKS_INTRO =
  "What happens after you sign up: connect your accounts, launch and monitor ads, then act on AI recommendations you approve.";

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    id: "connect",
    label: "Connect",
    summary: "Link your Meta and Google Ads accounts securely via OAuth.",
    title: "Connect your ad accounts",
    desc: "Sign up with email or Google, no credit card needed. A short setup then walks you through connecting Meta and Google Ads, each through the platform's own sign-in. Skip either one and connect it later.",
    bullets: [
      "Sign in through OAuth: Sidestep never sees your Meta or Google password",
      "Choose which ad accounts to add, including client accounts under a Google Ads manager account",
      "Access tokens are encrypted at rest, and you can disconnect any account at any time",
    ],
    poster: {
      headline: "Every ad account in one workspace",
      line: "Sign in with Meta and Google. Sidestep never sees your password.",
      features: ["Meta Ads", "Google Ads", "Secure OAuth"],
    },
  },
  {
    id: "create-monitor",
    label: "Create & monitor",
    summary: "Launch single or bulk ads and see every account in one view.",
    title: "Create ads and watch every account",
    desc: `Build Meta ads one at a time or up to ${MAX_BULK_PUBLISH} in a batch, and turn a Google ad group's keywords into a search ad, published paused for your review. Then follow Meta and Google performance side by side, from every account down to a single ad.`,
    bullets: [
      `Image, video and carousel Meta ads, up to ${MAX_BULK_PUBLISH} per publish`,
      "Pause, enable and change budgets on both platforms",
      "Alerts when a metric crosses a threshold you set",
    ],
    poster: {
      headline: "Launch faster, see every account at once",
      line: "Build Meta ads in bulk, then track Meta and Google side by side.",
      features: ["Meta Bulk Ad Builder", "Google Search Ad Builder", "Portfolio Overview", "Performance Alerts"],
    },
  },
  {
    id: "optimize",
    label: "Optimize",
    summary: "Act on AI recommendations grounded in your real account data, in one click.",
    title: "Optimize with Sidestep AI",
    desc: "Sidestep AI works from your live performance, each account's goal and target CPA, brand context scanned from your website, and recent account changes. It finds wasted search terms, suggests stronger search-ad copy and drafts on-brand ads. Changes apply only when you approve them.",
    bullets: [
      "Review keyword and search-ad suggestions, then apply them to Google Ads in one click",
      "On-brand ad copy written from your brand context and the creative itself",
      "Ask Sidestep AI about any account's performance in plain English",
    ],
    poster: {
      headline: "AI suggests. You approve.",
      line: "Recommendations built on your live data. Nothing changes until you say so.",
      features: ["Ask Sidestep AI", "Keyword Optimizer", "Search Ad Analyzer", "AI Ad Copywriter"],
    },
  },
];

/** The three steps shown inside the homepage's closing call to action. */
export const HOME_STEPS: HomeStep[] = HOW_IT_WORKS_STEPS.map((s) => ({ title: s.label, desc: s.summary }));

/** "1 seat" / "3 seats" per plan, read from the same limits the pricing cards show. */
const seatsByPlan = PLANS.flatMap((p) => {
  const value = p.limits.find((l) => l.label === "Team seats")?.value;
  return value ? [{ plan: p.name, seats: `${value} ${value === "1" ? "seat" : "seats"}` }] : [];
});

export const HOW_IT_WORKS_TEAM = {
  title: "Invite teammates when you're ready",
  intro: "Whoever signs up owns the workspace. Invite everyone else by email and give each person a role; you all work on the same ad accounts, tasks and notes.",
  roles: [
    { name: "Admin", desc: "Manages the team, workspace settings and connected accounts" },
    { name: "Editor", desc: "Creates and manages campaigns and ads, and uses the AI tools" },
    { name: "Viewer", desc: "Read-only access to campaigns, ads and performance" },
  ],
  seatsByPlan,
};

const planNamesPhrase = PLANS.map((p) => p.name).reduce(
  (acc, name, i, all) => (i === 0 ? name : `${acc}${i === all.length - 1 ? " or " : ", "}${name}`),
  "",
);

/** Shared with the Features page's FAQ. */
const AI_APPROVAL_FAQ: FaqItem = {
  question: "Does AI change my campaigns without asking?",
  answer: "No. Keyword Optimizer and Search Ad Analyzer suggestions apply only after you approve them, and the Google Search Ad Builder publishes ads paused. Ask Sidestep AI reads your data but never edits your accounts. Automations act only on rules you write.",
};

export const HOW_IT_WORKS_FAQ_ITEMS: FaqItem[] = [
  {
    question: "Do I need a credit card to start?",
    answer: `No. The ${TRIAL_SHORT} includes full ${TRIAL_PLAN_NAME} access with no card. You add payment only when you choose a plan.`,
  },
  {
    question: "What happens when the trial ends?",
    answer: `Choose ${planNamesPhrase} to keep using Sidestep; your workspace and data stay as you left them. If you're a first-time paying customer, your first subscription payment is then covered by a separate ${GUARANTEE_SHORT}.`,
  },
  AI_APPROVAL_FAQ,
];

const HOW_IT_WORKS_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    breadcrumb(["How It Works", "/how-it-works"]),
    {
      "@type": "HowTo",
      name: "How to get started with Sidestep Ads",
      description:
        "What happens after you sign up for Sidestep Ads: connect your Meta and Google Ads accounts, create and monitor ads, then optimize with AI recommendations you approve.",
      step: HOW_IT_WORKS_STEPS.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.title,
        text: s.desc,
      })),
    },
    faqPage(HOW_IT_WORKS_FAQ_ITEMS),
  ],
};

// ─── Bulk Ad Builder ──────────────────────────────────────────────────────

export const BULK_AD_BUILDER_HERO_BULLETS = [
  "Build single-image, video, and carousel ads in bulk",
  "Generate multiple primary texts and headlines with AI",
  // The hero states the top-tier ceiling only ("up to 100"); the per-plan
  // breakdown stays in the capacity table further down so a cold visitor isn't
  // asked to compare plans before they're sold.
  `Publish up to ${MAX_BULK_PUBLISH} ads per batch`,
];

export interface BulkAdBuilderStep {
  num: string;
  title: string;
  desc: string;
}

export const BULK_AD_BUILDER_STEPS: BulkAdBuilderStep[] = [
  {
    num: "01",
    title: "Choose the destination",
    desc: "Select the Meta ad account, campaign, and ad set you want to publish into, or create the campaign structure before building the batch.",
  },
  {
    num: "02",
    title: "Build the batch",
    desc: "Add rows for each ad, choose image, video, or carousel format, and enter copy, headlines, CTA, destination URL, and creative assets inline.",
  },
  {
    num: "03",
    title: "Add variations & defaults",
    desc: "Add multiple primary texts and headlines, generate on-brand options with AI, and apply saved templates, UTMs, snippets, and enhancement preferences.",
  },
  {
    num: "04",
    title: "Validate & publish",
    desc: "Review the batch, fix missing fields or validation issues, select the ads you're ready to launch, and publish them directly to Meta with live status updates.",
  },
];

export const BULK_AD_BUILDER_DEEP_DIVE = [
  {
    title: "Image, video, and carousel ads",
    body: "Build single-image and video ads, or create carousels with up to 10 cards, each with its own creative, headline, description, link, and CTA.",
  },
  {
    title: "Multiple copy variations and AI assistance",
    body: "Add several Primary Text and Headline options to each ad, or generate brand-aware variations based on your website, brand context, and existing top ads.",
  },
  {
    title: "Your defaults, preloaded",
    body: "Save enhancement preferences, UTM templates, text snippets, account defaults, and reusable templates so each batch starts closer to complete.",
  },
];

// What the buyer actually gets for one flat price — stacked, not menu'd, so
// the Starter anchor reads as trivial against the pile.
export const BULK_AD_BUILDER_VALUE_STACK = [
  "Meta bulk ad creation and publishing",
  "Single-image, video, and 10-card carousel ads",
  "Multiple primary texts and headlines per ad",
  "Brand-aware AI copy generation",
  "AI image generation",
  "Templates, presets, account defaults, and UTM templates",
  "Meta and Google Ads reporting in one workspace",
  "Performance alerts and AI campaign insights",
  "Multi-account and team collaboration",
];

const planLimitsSentence = PLANS.map(
  (p) =>
    `${p.name} up to ${p.adsPerBulkPublish} ads per batch (${p.adsPer24h.toLocaleString("en-US")} per 24 hours)`
).join(", ");

// "up to 3 accounts on Starter, 15 on Pro, and 100 on Agency" — read from the
// same limits the pricing cards render so the numbers can't drift.
const connectedAccountsPhrase = PLANS
  .map((p) => ({ name: p.name, value: p.limits.find((l) => l.label === "Connected ad accounts")?.value }))
  .filter((p): p is { name: string; value: string } => Boolean(p.value))
  .map((p, i, arr) => `${i === arr.length - 1 ? "and " : ""}${i === 0 ? `up to ${p.value} accounts on` : `${p.value} on`} ${p.name}`)
  .join(", ");

export const BULK_AD_BUILDER_FAQ_ITEMS: FaqItem[] = [
  {
    question: "Is a credit card required for the trial?",
    answer: `No. Your ${TRIAL_DAYS}-day trial includes full ${TRIAL_PLAN_NAME} access with no credit card required — you only add payment when you choose a plan.`,
  },
  {
    question: "Can Sidestep publish ads without my approval?",
    answer: "No. You review and validate the batch before anything is published. Sidestep highlights missing fields and other issues so you can correct them before sending the selected ads to Meta.",
  },
  {
    question: "Does the Bulk Ad Builder work with Google Ads?",
    answer: "The Bulk Ad Builder currently creates and publishes Meta ads. Sidestep also connects Google Ads so you can monitor Meta and Google performance across accounts from the same workspace.",
  },
  {
    question: "Can I use the builder across multiple accounts?",
    answer: `Yes. Connect multiple ad accounts and publish into any of them from one workspace — ${connectedAccountsPhrase || "connected-account limits scale with your plan"}. Invite teammates so the whole roster runs from one place.`,
  },
  {
    question: "Does Sidestep replace Ads Manager?",
    answer: "No. Sidestep publishes directly into your Meta campaigns and ad sets, so every ad lands in Ads Manager — correctly formatted and targeted. Use Sidestep for the fast build-and-launch and keep Ads Manager for everything else.",
  },
  {
    question: "What formats are supported?",
    answer: "Single-image ads, video ads, and carousels with up to 10 cards — each card with its own creative, headline, description, link, and CTA.",
  },
  {
    question: "What are the per-plan publishing limits?",
    answer: `${planLimitsSentence}. Every plan includes every feature.`,
  },
  {
    question: "What if Sidestep isn't a fit?",
    answer: `If you find that out during the ${TRIAL_SHORT}, it costs you nothing: there's no credit card, so there's nothing to cancel or refund. If you do subscribe, your first subscription payment is backed by a separate ${GUARANTEE_SHORT}: email us within ${GUARANTEE_DAYS} days of that first payment for a full refund — no hoops.`,
  },
];

const BULK_AD_BUILDER_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    breadcrumb(["Features", "/features"], ["Meta Bulk Ad Builder", "/features/bulk-ad-builder"]),
    faqPage(BULK_AD_BUILDER_FAQ_ITEMS),
  ],
};

// ─── Features ─────────────────────────────────────────────────────────────

// The full feature list, checked in here rather than fetched from the app's
// feature registry: the page and its crawler copy both read this one list, so
// every feature a visitor sees is one a crawler sees. Each line was checked
// against the app (RFlagg88/sidestep); keep it that way when you edit it, and
// never name the AI providers behind the features.

/** Icon keys the Features page maps to lucide icons (see FEATURE_ICONS there). */
export type FeatureIconKey =
  | "sheet" | "table" | "layers" | "search" | "copy" | "users-round" | "download" | "hard-drive"
  | "power" | "wallet" | "pencil" | "workflow"
  | "layout-grid" | "gauge" | "trending-up" | "bell-ring" | "mail" | "calculator"
  | "message-square-text" | "key-round" | "scan-search" | "pen-line" | "image" | "globe" | "target"
  | "list-checks" | "sticky-note" | "shield-check" | "inbox" | "history";

export interface FeatureItem {
  name: string;
  icon: FeatureIconKey;
  /** Which ad platforms it works with; omitted when it isn't platform-specific. */
  platform?: "Meta" | "Google" | "Meta & Google";
  badge?: "Beta";
  /** One-sentence benefit, shown under the name. */
  line: string;
  /** Spotlight features only: the checklist under the line. */
  details?: string[];
  /** Spotlight features only: a deeper page about this feature. */
  link?: { label: string; to: string };
}

export interface FeatureCategory {
  /** Anchor id on the Features page. */
  id: "launch" | "manage" | "monitor" | "optimize" | "collaborate";
  /** Matches the homepage carousel's categories. */
  name: HomeCapability["category"];
  headline: string;
  intro: string;
  /** The category's lead feature, shown large with an animation. */
  spotlight: FeatureItem;
  /** Everything else in the category, listed beside the spotlight. */
  features: FeatureItem[];
}

export const FEATURE_CATEGORIES: FeatureCategory[] = [
  {
    id: "launch",
    name: "Launch",
    headline: "Build in batches, publish in one go",
    intro: "Get ads live without clicking through Ads Manager one field at a time.",
    spotlight: {
      name: "Meta Bulk Ad Builder",
      icon: "sheet",
      platform: "Meta",
      line: `Build up to ${MAX_BULK_PUBLISH} Meta ads at once in a spreadsheet-style builder, then publish the whole batch.`,
      details: [
        "Image, video and carousel ads, up to 10 cards",
        "Up to 5 primary texts and 5 headlines per ad",
        "Every row checked before anything publishes",
      ],
      link: { label: "See the Bulk Ad Builder", to: "/features/bulk-ad-builder" },
    },
    features: [
      { name: "Campaign & Ad Set Builder", icon: "layers", platform: "Meta", line: "Set up campaigns and ad sets, from objective and budget to audience, schedule and placements." },
      { name: "Google Search Ad Builder", icon: "search", platform: "Google", line: "Turn an ad group's keywords and landing page into a full search ad: up to 15 headlines and 4 descriptions, published paused for your review." },
      { name: "Ad Templates", icon: "copy", platform: "Meta & Google", line: "Save winning ads, carousels and search ads included, and start the next launch from them." },
      { name: "Audience Presets", icon: "users-round", platform: "Meta", line: "Save locations, demographics, interests and custom audiences as one-click targeting presets." },
      { name: "Import Existing Ads", icon: "download", platform: "Meta", line: "Pull any live Meta ad into the builder with every field filled in, then spin up variations." },
      { name: "Google Drive", icon: "hard-drive", line: "Pick images and videos straight from Google Drive, shared drives included. No download, no re-upload." },
    ],
  },
  {
    id: "manage",
    name: "Manage",
    headline: "Make changes without switching platforms",
    intro: "Budgets, statuses and settings for every connected account, edited where you already are.",
    spotlight: {
      name: "Automations",
      icon: "workflow",
      platform: "Meta & Google",
      badge: "Beta",
      line: "Write rules on CPA, ROAS, spend and more. When one matches, Sidestep pauses, creates a task or notifies you.",
      details: [
        "Match all or any of several conditions",
        "Run on one account or all of them",
        "In-app and email notifications",
      ],
    },
    features: [
      { name: "Status Changes", icon: "power", platform: "Meta & Google", line: "Pause or enable campaigns, ad sets, ads and Performance Max asset groups from your performance tables." },
      { name: "Budget Edits", icon: "wallet", platform: "Meta & Google", line: "Adjust daily and lifetime budgets in Sidestep and the change lands on the platform." },
      { name: "Edit Live Ads", icon: "pencil", platform: "Meta", line: "Change names, budgets, bids, targeting, schedules and attribution on live campaigns and ad sets." },
    ],
  },
  {
    id: "monitor",
    name: "Monitor",
    headline: "See what needs attention first",
    intro: "Every account's numbers in one place, with the problems surfaced instead of buried.",
    spotlight: {
      name: "Portfolio Snapshot",
      icon: "gauge",
      platform: "Meta & Google",
      line: "Start the day with accounts sorted into Needs Attention, Watch List and Opportunities, judged against your goals.",
      details: ["Sits next to your open tasks and alerts", "Grades each account against its target"],
    },
    features: [
      { name: "Portfolio Overview", icon: "layout-grid", platform: "Meta & Google", line: "Spend, results, CPA and ROAS for every connected account in one view, with unusual CPAs flagged." },
      { name: "Performance Tables", icon: "table", platform: "Meta & Google", line: "Drill from campaign to ad set to ad, with custom date ranges and period-over-period comparison." },
      { name: "Trend Overview", icon: "trending-up", platform: "Meta & Google", line: "CPA over 30, 14, 7 and 3 days, yesterday and today, side by side, so a shift shows before it costs you." },
      { name: "Performance Alerts", icon: "bell-ring", platform: "Meta & Google", line: "Set thresholds on spend, CPA, ROAS, CTR and more for each campaign, and hear about it when one is crossed." },
      { name: "Scheduled Reports", icon: "mail", platform: "Meta & Google", line: "Portfolio, performance, trend and alert reports emailed to you or your clients, daily or weekly." },
      { name: "Custom Columns & Formatting", icon: "calculator", platform: "Meta & Google", line: "Build formula columns like blended ROAS and colour-code any metric so good and bad numbers stand out." },
    ],
  },
  {
    id: "optimize",
    name: "Optimize",
    headline: "AI that knows your accounts",
    intro: "Recommendations built on your live data, brand and goals. You review, then apply.",
    spotlight: {
      name: "Ask Sidestep AI",
      icon: "message-square-text",
      platform: "Meta & Google",
      line: "Ask about your accounts in plain English. Answers draw on your live data, goals and recent changes.",
      details: [
        "Compare up to 12 accounts in one conversation",
        "Analyze Account: winners, losers, budget moves and next steps",
        "Export an analysis as a PDF",
      ],
    },
    features: [
      { name: "Keyword Optimizer", icon: "key-round", platform: "Google", line: "Find wasted search terms, then pause keywords, add new ones and add negatives. Review first, apply in one click." },
      { name: "Search Ad Analyzer", icon: "scan-search", platform: "Google", line: "Uses Google's asset ratings and Ad Strength to suggest stronger headlines and descriptions, then applies them." },
      { name: "AI Ad Copywriter", icon: "pen-line", platform: "Meta & Google", line: "Primary text, headlines and descriptions in your brand's voice, written from your brand context and the creative itself." },
      { name: "AI Image Generator", icon: "image", line: "Generate ad images from a prompt in square, portrait and story sizes, with style presets and your product photos." },
      { name: "Brand Context", icon: "globe", line: "Scan your website once. Every AI feature then writes and analyzes with your voice, offers and audience." },
      { name: "Account Goals", icon: "target", platform: "Meta & Google", line: "Set each account's goal (lead cost, lead volume or ROAS) and target CPA; Sidestep judges performance against it." },
    ],
  },
  {
    id: "collaborate",
    name: "Collaborate",
    headline: "Keep the team and the work together",
    intro: "Tasks, notes and access live next to the accounts they're about, not in another tool.",
    spotlight: {
      name: "Tasks",
      icon: "list-checks",
      line: "A task board for ad work, by account, due date or priority, with checklists, templates and @mentions.",
      details: ["Board and list views", "Due-date reminders"],
    },
    features: [
      { name: "Notes", icon: "sticky-note", line: "Pin notes to an account or campaign so the reason behind every change stays with it." },
      { name: "Team Access & Roles", icon: "shield-check", line: "Invite teammates as owner, admin, editor or viewer, with every client account in one workspace." },
      { name: "Notifications", icon: "inbox", line: "Alerts, @mentions and due tasks in one inbox, one click from the source." },
      { name: "Activity Log", icon: "history", line: "See who changed what, and when, across the workspace." },
    ],
  },
];

/**
 * Every feature on the Features page — the homepage's "Explore all N features" link reads it.
 * The Features share image (public/og/features-og.jpg) bakes in "30 features"; remake it when this changes.
 */
export const FEATURE_COUNT = FEATURE_CATEGORIES.reduce((n, c) => n + 1 + c.features.length, 0);

/** "150, 600 or 3,000" — monthly AI credits by plan, read from the pricing cards. */
const aiCreditValues = PLANS.map((p) => p.limits.find((l) => l.label === "AI credits / month")?.value)
  .filter((v): v is string => Boolean(v));
const aiCreditsPhrase = aiCreditValues.length > 1
  ? `${aiCreditValues.slice(0, -1).join(", ")} or ${aiCreditValues[aiCreditValues.length - 1]}`
  : aiCreditValues.join("");

export const FEATURES_FAQ_ITEMS: FaqItem[] = [
  {
    question: "Are all features included on every plan?",
    answer: "Yes. Every plan includes every feature. Plans differ only in capacity: connected ad accounts, team seats, ads per bulk publish, ads per 24 hours and monthly AI credits.",
  },
  {
    question: "Which features work with Google Ads?",
    answer: "Monitoring, budget and status changes, alerts, reports, Automations and the AI tools work across Meta and Google Ads. The Google Search Ad Builder, Keyword Optimizer and Search Ad Analyzer are built for Google. The Bulk Ad Builder and campaign builder publish to Meta.",
  },
  AI_APPROVAL_FAQ,
  {
    question: "How do AI credits work?",
    answer: `Each plan includes a monthly allowance of Sidestep AI credits${aiCreditsPhrase ? ` (${aiCreditsPhrase} by plan)` : ""} that the AI features draw from. You can buy top-up packs at any time.`,
  },
];

const featureBullet = (f: FeatureItem) => `${f.name}${f.platform ? ` (${f.platform})` : ""} — ${f.line}`;

const FEATURE_CATEGORY_SECTIONS: CrawlerSection[] = FEATURE_CATEGORIES.map((c) => ({
  heading: `${c.name}: ${c.headline}`,
  paragraphs: [
    c.intro,
    ...(c.spotlight.link ? [`${c.spotlight.name}: ${SITE_URL}${c.spotlight.link.to}`] : []),
  ],
  bullets: [c.spotlight, ...c.features].map(featureBullet),
}));

// One description of the app, shared by the homepage and the Features page.
// Its featureList is every feature on the Features page, so the structured
// data can't drift from the page the way a hand-kept list did.
const SOFTWARE_APPLICATION = {
  "@type": "SoftwareApplication",
  name: "Sidestep Ads",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "AI-powered platform for creating and managing Meta and Google Ads. Launch ads faster, monitor performance, optimize keywords, and manage budgets — all from one dashboard.",
  url: SITE_URL,
  offers: {
    "@type": "AggregateOffer",
    lowPrice: (PLANS[0].annualPrice / 12).toFixed(2),
    highPrice: String(PLANS[PLANS.length - 1].monthlyPrice),
    priceCurrency: "USD",
    offerCount: String(PLANS.length),
    description: `${TRIAL_SHORT}, no credit card required. Plans from ${formatPrice(PLANS[0], "annual")}/mo billed annually.`,
  },
  featureList: FEATURE_CATEGORIES.flatMap((c) => [c.spotlight, ...c.features]).map(
    (f) => `${f.name}${f.platform ? ` (${f.platform})` : ""}`,
  ),
};

const FEATURES_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [breadcrumb(["Features", "/features"]), SOFTWARE_APPLICATION, faqPage(FEATURES_FAQ_ITEMS)],
};

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      name: "Sidestep Ads",
      url: SITE_URL,
      logo: `${SITE_URL}/logo-512.webp`,
      description: "AI-powered platform for creating and managing Meta and Google Ads",
      foundingDate: "2024",
      email: SUPPORT_EMAIL,
      telephone: SUPPORT_PHONE_E164,
      sameAs: [],
    },
    {
      "@type": "WebSite",
      name: "Sidestep Ads",
      url: SITE_URL,
      description: "Create, Optimize & Monitor Meta and Google Ads",
      publisher: { "@type": "Organization", name: "Sidestep Ads" },
    },
    SOFTWARE_APPLICATION,
    faqPage(HOME_FAQ_ITEMS),
  ],
};

// ─── Contact & legal ──────────────────────────────────────────────────────

const CONTACT_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      url: `${SITE_URL}/contact`,
      mainEntity: {
        "@type": "LocalBusiness",
        name: "Sidestep Ads",
        telephone: SUPPORT_PHONE_E164,
        email: SUPPORT_EMAIL,
        url: SITE_URL,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Portland",
          addressRegion: "OR",
          addressCountry: "US",
        },
        openingHours: ["Mo-Fr 09:00-17:00"],
      },
    },
  ],
};

const legalPage = (
  path: string,
  crumb: string,
  name: string,
  description: string,
  articleName: string,
  dates: { datePublished: string; dateModified?: string }
) => ({
  "@context": "https://schema.org",
  "@graph": [
    breadcrumb([crumb, path]),
    {
      "@type": "WebPage",
      name,
      description,
      url: `${SITE_URL}${path}`,
      mainEntity: {
        "@type": "Article",
        name: articleName,
        ...dates,
        publisher: { "@type": "Organization", name: "Soteros Consulting LLC" },
      },
    },
  ],
});

// ─── Route map ────────────────────────────────────────────────────────────

export const ROUTE_CONTENT: Record<string, RouteContent> = {
  "/": {
    navLabel: "Home",
    intro:
      "Launch ads faster, monitor performance clearly, and optimize with AI grounded in your real account data. One workspace for Meta and Google Ads, built for brands, freelancers, in-house teams, and agencies.",
    sections: [
      {
        heading: "The old way vs. the Sidestep way",
        bullets: HOME_COMPARISON.map((r) => `${r.old} → ${r.next}`),
      },
      {
        heading: "One workspace for the whole job",
        bullets: HOME_CAPABILITIES.map((c) => `${c.category}: ${c.title} — ${c.desc} (${c.features.join(", ")})`),
      },
      {
        heading: "Built for teams who run paid ads",
        bullets: HOME_AUDIENCES.map((a) => `${a.title} — ${a.desc}`),
      },
      {
        heading: "Up and running in minutes",
        ordered: true,
        bullets: HOME_STEPS.map((s) => `${s.title} — ${s.desc}`),
      },
      pricingSection,
    ],
    faq: HOME_FAQ_ITEMS,
    jsonLd: HOME_JSON_LD,
  },

  "/features": {
    navLabel: "Features",
    intro:
      `Every Sidestep feature for Meta and Google Ads, grouped by the job it does: launch, manage, monitor, optimize and collaborate. ${FEATURE_COUNT} features, all included on every plan.`,
    sections: [...FEATURE_CATEGORY_SECTIONS, pricingSection],
    faq: FEATURES_FAQ_ITEMS,
    jsonLd: FEATURES_JSON_LD,
  },

  "/features/bulk-ad-builder": {
    navLabel: "Meta Bulk Ad Builder",
    intro:
      "Build, edit, and publish entire batches of Meta ads from one spreadsheet-style workspace. Generate on-brand copy, validate every row, and launch directly to your campaigns and ad sets.",
    sections: [
      { heading: "What you can build", bullets: BULK_AD_BUILDER_HERO_BULLETS },
      {
        heading: "How the Bulk Ad Builder works",
        ordered: true,
        bullets: BULK_AD_BUILDER_STEPS.map((s) => `${s.title} — ${s.desc}`),
      },
      {
        heading: "Built for real launch days",
        bullets: BULK_AD_BUILDER_DEEP_DIVE.map((d) => `${d.title} — ${d.body}`),
      },
      {
        heading: "Bulk ad creation & 30+ other paid media features included",
        paragraphs: [
          "Build and publish your Meta ads, monitor Meta and Google performance, use AI analysis tools, and manage your paid media workflow from one platform. Built for brands, freelancers, in-house teams, and agencies.",
        ],
        bullets: BULK_AD_BUILDER_VALUE_STACK,
      },
      pricingSection,
    ],
    faq: BULK_AD_BUILDER_FAQ_ITEMS,
    jsonLd: BULK_AD_BUILDER_JSON_LD,
  },

  "/how-it-works": {
    navLabel: "How It Works",
    intro: HOW_IT_WORKS_INTRO,
    sections: [
      ...HOW_IT_WORKS_STEPS.map((s, i) => ({
        heading: `Step ${i + 1}: ${s.title}`,
        paragraphs: [s.desc],
        bullets: s.bullets,
      })),
      {
        heading: HOW_IT_WORKS_TEAM.title,
        paragraphs: [HOW_IT_WORKS_TEAM.intro],
        bullets: [
          ...HOW_IT_WORKS_TEAM.roles.map((r) => `${r.name} — ${r.desc}`),
          `Team seats: ${HOW_IT_WORKS_TEAM.seatsByPlan.map((s) => `${s.plan} ${s.seats}`).join(", ")}`,
        ],
      },
    ],
    faq: HOW_IT_WORKS_FAQ_ITEMS,
    jsonLd: HOW_IT_WORKS_JSON_LD,
  },

  "/pricing": {
    navLabel: "Pricing",
    intro:
      `${PRICE_SUMMARY} Plans differ only in connected ad accounts, team seats, publishing volume and monthly AI credits.`,
    sections: [
      {
        heading: "Compare plans",
        paragraphs: [`Prices in USD, as of ${PRICES_AS_OF_LABEL}. Yearly billing is charged once a year. ${RENEWAL_TERMS}`],
        table: planComparisonTable,
      },
      pricingSection,
      {
        heading: "Included on every plan",
        bullets: FEATURE_CATEGORIES.map((c) => `${c.name}: ${[c.spotlight, ...c.features].map((f) => f.name).join(", ")}`),
      },
      {
        heading: "Larger rosters",
        paragraphs: [
          `Have more than ${MAX_CONNECTED_ACCOUNTS} ad accounts? Contact us at ${SITE_URL}/contact.`,
        ],
      },
      {
        heading: "Trial and guarantee",
        paragraphs: [
          `The ${TRIAL_SHORT} costs nothing and requires no credit card. Separately, a ${GUARANTEE_SHORT} covers a first-time customer's first subscription payment: email ${SUPPORT_EMAIL} within ${GUARANTEE_DAYS} days of that payment for a full refund. Full terms at ${SITE_URL}/refund-policy.`,
        ],
      },
    ],
    faq: PRICING_FAQ_ITEMS,
    jsonLd: PRICING_JSON_LD,
  },

  "/contact": {
    navLabel: "Contact",
    intro:
      "Get in touch with the Sidestep Ads team. We're here to help with your Meta and Google Ads questions, support requests, and partnership inquiries.",
    sections: [
      {
        heading: "Contact details",
        bullets: [
          `Email: ${SUPPORT_EMAIL}`,
          `Phone: ${SUPPORT_PHONE_DISPLAY}`,
          `Business hours: ${SUPPORT_HOURS}`,
          `Location: ${SUPPORT_LOCATION}`,
        ],
      },
    ],
    jsonLd: CONTACT_JSON_LD,
  },

  "/privacy": {
    navLabel: "Privacy Policy",
    intro:
      "How Sidestep Ads collects, uses, and protects your personal information and data from connected services like Google Drive and Meta.",
    sections: [
      {
        heading: "Google user data sharing",
        paragraphs: [
          "Sidestep Ads may access Google user data only after you authorize a Google connection. We do not sell Google user data. We share Google user data only at your direction to provide the Service, with service providers that help us operate Sidestep Ads, for legal and safety reasons, and during business transfers such as mergers or acquisitions.",
          "Our use of Google APIs adheres to the Google API Services User Data Policy, including Limited Use requirements.",
        ],
      },
    ],
    jsonLd: legalPage(
      "/privacy",
      "Privacy Policy",
      "Privacy Policy - Sidestep Ads",
      "Learn how Sidestep Ads collects, uses, and protects your personal information and data from connected services like Google Drive and Meta.",
      "Privacy Policy for Sidestep Ads",
      { datePublished: "2025-10-22", dateModified: "2026-10-26" }
    ),
  },

  "/terms": {
    navLabel: "Terms of Service",
    intro:
      "Terms and conditions for using Sidestep Ads, including acceptable use, third-party integrations, and user responsibilities.",
    sections: [],
    jsonLd: legalPage(
      "/terms",
      "Terms of Service",
      "Terms of Service - Sidestep Ads",
      "Terms and conditions for using Sidestep Ads, including acceptable use, third-party integrations, and user responsibilities.",
      "Terms of Service for Sidestep Ads",
      { datePublished: "2025-10-22", dateModified: "2026-06-24" }
    ),
  },

  "/refund-policy": {
    navLabel: "Money-Back Guarantee & Refund Policy",
    intro:
      `Sidestep Ads' ${GUARANTEE_SHORT} and refund policy: who's eligible, how to request a refund, and what's covered.`,
    sections: [
      {
        heading: "How the guarantee works",
        paragraphs: [
          `The ${TRIAL_SHORT} requires no credit card, so there is nothing to cancel or refund during the trial. When you become a paying customer, your first subscription payment on any plan is covered by a separate ${GUARANTEE_SHORT}: email ${SUPPORT_EMAIL} within ${GUARANTEE_DAYS} days of that first payment for a full refund.`,
        ],
      },
    ],
    jsonLd: legalPage(
      "/refund-policy",
      "Money-Back Guarantee & Refund Policy",
      "Money-Back Guarantee & Refund Policy - Sidestep Ads",
      "Sidestep Ads' 30-day money-back guarantee and refund policy: who's eligible, how to request a refund, and what's covered.",
      "Money-Back Guarantee & Refund Policy for Sidestep Ads",
      { datePublished: "2026-06-24", dateModified: "2026-10-26" }
    ),
  },
};
